import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '@/utils/constants';

/**
 * Route configuration for breadcrumb labels
 * Maps path segments to human-readable labels
 */
const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  detections: 'Detections',
  takedowns: 'Takedowns',
  campaigns: 'Campaigns',
  // Intelligence overview + its axes/dossiers
  intelligence: 'Intelligence',
  botnets: 'Botnets',
  kits: 'Phishing kits',
  actors: 'Threat actors',
  traps: 'Honeytokens',
  services: 'Services',
  dmarc: 'DMARC',
  reports: 'Reports',
  analysis: 'Analysis',
  unauthorized: 'Unauthorized Sources',
  honeytokens: 'Honeytokens',
  triggers: 'Triggers',
  analytics: 'Analytics',
  emails: 'Emails',
  users: 'Users',
  company: 'Company',
  settings: 'Settings',
  auth: 'Authentication',
  login: 'Login',
  register: 'Register',
};

interface BreadcrumbItem {
  label: string;
  path: string;
  isLast: boolean;
}

interface BreadcrumbsProps {
  className?: string;
  /** Override automatic breadcrumb generation with custom items */
  customItems?: Array<{ label: string; path?: string }>;
}

/**
 * Dynamic breadcrumbs — mono, muted (fg-3), em-dash/slash separators, hover
 * turns accent. No pills, no MT chrome (terminal-on-OLED reskin).
 */
export function Breadcrumbs({
  className = '',
  customItems,
}: BreadcrumbsProps): React.ReactElement {
  const location = useLocation();

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    if (customItems) {
      return customItems.map((item, index) => ({
        label: item.label,
        path: item.path || '#',
        isLast: index === customItems.length - 1,
      }));
    }

    const pathSegments = location.pathname.split('/').filter(Boolean);

    if (pathSegments.length === 0) {
      return [];
    }

    const items: BreadcrumbItem[] = [];
    let currentPath = '';

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        segment
      );

      if (isUUID) {
        items.push({
          label: 'Details',
          path: currentPath,
          isLast: index === pathSegments.length - 1,
        });
      } else {
        const label =
          ROUTE_LABELS[segment] ||
          segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

        items.push({
          label,
          path: currentPath,
          isLast: index === pathSegments.length - 1,
        });
      }
    });

    return items;
  }, [location.pathname, customItems]);

  // Don't render if only dashboard or no items
  if (breadcrumbItems.length <= 1 && !customItems) {
    return <></>;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center flex-wrap font-mono text-[12px] text-phish-fg-3 ${className}`}
    >
      <Link
        to={ROUTES.DASHBOARD}
        className="text-phish-fg-3 hover:text-phish-accent transition-colors duration-phish"
      >
        Home
      </Link>
      {breadcrumbItems.map((item) => (
        <React.Fragment key={item.path}>
          <span className="mx-2 text-phish-fg-3" aria-hidden>
            —
          </span>
          {item.isLast ? (
            <span className="text-phish-fg-1" aria-current="page">
              {item.label}
            </span>
          ) : (
            <Link
              to={item.path}
              className="text-phish-fg-3 hover:text-phish-accent transition-colors duration-phish"
            >
              {item.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

export default Breadcrumbs;
